import React from "react";
import { createRoot, Root } from "react-dom/client";
import { ThemeProvider, GlobalStyles, createTheme } from "@mui/material";
import Combo, { Opt } from "./Combo";
import { loadBoards, MappedBoard } from "./boards";

const theme = createTheme({ palette: { mode: "light" } });

function fmt(n: number | null | undefined): string {
    return (n == null) ? "" : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// One React root per mount container.
const roots = new WeakMap<HTMLElement, Root>();
function render(container: HTMLElement, node: React.ReactNode): void {
    let root = roots.get(container);
    if (!root) { root = createRoot(container); roots.set(container, root); }
    root.render(
        <ThemeProvider theme={theme}>
            {/* No CssBaseline — it sets global body styles (white bg) and would bleed onto the page.
                Just a scoped fix so the page's bare-<legend> CSS doesn't paint MUI's outline legend. */}
            <GlobalStyles styles={{ ".ucsq-combo legend": { border: 0, padding: 0, margin: 0, background: "none" } }} />
            {node}
        </ThemeProvider>
    );
}
function unmount(container: HTMLElement): void {
    const root = roots.get(container);
    if (root) { root.unmount(); roots.delete(container); }
}

// Preload the full board catalogue once (like the configurator's DatabaseHandler).
let boardsPromise: Promise<MappedBoard[]> | null = null;
function boards(): Promise<MappedBoard[]> {
    if (!boardsPromise) {
        const shop = Number((window as any).__UCSQ_SHOP) || 1;
        boardsPromise = loadBoards(shop);
    }
    return boardsPromise;
}

function boardOptions(list: MappedBoard[], suggestedIds: string[]): Opt[] {
    const sug = new Set(suggestedIds);
    const opts: Opt[] = list.map((b) => ({
        id: b.id_board,
        label: b.name + " " + (b.thickness ?? "") + " " + (b.m2_price ?? ""),
        primary: b.name,
        secondary: (b.thickness != null ? b.thickness + " mm" : "")
            + (b.m2_price != null ? " · " + fmt(b.m2_price) + " Ft/m²" : ""),
        cm: b.calc_method,
        group: sug.has(b.id_board) ? "Javaslatok" : "Katalógus",
    }));
    opts.sort((a, b) => {
        const ga = a.group === "Javaslatok" ? 0 : 1, gb = b.group === "Javaslatok" ? 0 : 1;
        return ga !== gb ? ga - gb : a.primary.localeCompare(b.primary, "hu");
    });
    return opts;
}

interface EdgeOpt {
    id_edge: string; name: string; thumb: string; thickness: number; width: number; price_fm: number | null;
    /** the chosen board lists this edge as compatible → ranked into the top group */
    compatible?: boolean;
}

/** Public API the standalone smart-quote page (vanilla JS) calls to mount MUI comboboxes. */
const api = {
    /** Kick off (or reuse) the board preload. */
    init(shopId?: number): Promise<unknown> {
        if (shopId != null) (window as any).__UCSQ_SHOP = shopId;
        return boards();
    },

    /** Material selector: full catalogue, suggested boards grouped on top. onPick gets a MappedBoard. */
    async mountMaterial(container: HTMLElement, o: { suggestedIds?: string[]; onPick: (b: MappedBoard) => void }): Promise<void> {
        container.classList.add("ucsq-combo");
        const list = await boards();
        const byId = new Map(list.map((b) => [b.id_board, b]));
        const suggested = (o.suggestedIds || []).filter((id) => byId.has(id));
        render(container, (
            <Combo
                options={boardOptions(list, suggested)}
                placeholder="Anyag neve vagy kód…"
                grouped={suggested.length > 0}
                autoOpen
                onPick={(id) => { const b = byId.get(id); if (b) setTimeout(() => o.onPick(b), 0); }}
            />
        ));
    },

    /** Edge selector: options supplied by the caller (the board's compatible edges). */
    mountEdge(container: HTMLElement, o: { edges: EdgeOpt[]; onPick: (e: EdgeOpt) => void }): void {
        container.classList.add("ucsq-combo");
        const byId = new Map(o.edges.map((e) => [e.id_edge, e]));
        const hasCompatible = o.edges.some((e) => e.compatible);
        const opts: Opt[] = o.edges
            .map((e) => ({
                id: e.id_edge,
                label: e.name + " " + e.thickness + " " + e.width + " " + (e.price_fm ?? ""),
                primary: e.name,
                secondary: e.thickness + "×" + e.width + " mm" + (e.price_fm != null ? " · " + fmt(e.price_fm) + " Ft/fm" : ""),
                group: e.compatible ? "Ajánlott (kompatibilis)" : "Összes élzáró",
            }))
            .sort((a, b) => {
                // compatible edges first, then alphabetical within each group
                const ga = a.group === "Ajánlott (kompatibilis)" ? 0 : 1;
                const gb = b.group === "Ajánlott (kompatibilis)" ? 0 : 1;
                return ga !== gb ? ga - gb : a.primary.localeCompare(b.primary, "hu");
            });
        render(container, (
            <Combo
                options={opts}
                placeholder="Élzáró neve, vastagság…"
                grouped={hasCompatible}
                autoOpen
                onPick={(id) => { const e = byId.get(id); if (e) setTimeout(() => o.onPick(e), 0); }}
            />
        ));
    },

    unmount,
};

(window as any).UcsqCombo = api;

// Start preloading boards as soon as the bundle loads (shop id injected by the page).
boards();
